import { FaEllipsisH } from 'react-icons/fa';
import React, { useMemo, useState } from 'react';
import { startCase as _startCase, isObject as _isObject, keys as _keys, concat as _concat } from 'lodash';

import Collection from 'core/model/Collection';
import AdminTable from 'app/components/admin/AdminTable';
import { Flex, Heading, Text, Button, CopyText, ConfigSnippet } from 'core/components';
import ToolResultCard from '../../components/ToolResultCard';

const AzureResourceGraphToolResult = (props) => {
  const [showQuery, setShowQuery] = useState(false);

  const { tool, onCloseToolResult, isInlineMode } = props;
  const { raw_result, query_object } = tool;
  const { query, subscription_ids = [] } = query_object;
  const { data = [] } = raw_result || {};

  const collection = useMemo(() => {
    const coll = new Collection();

    if (_isObject(data)) {
      const mergedData = _concat(...Object.values(data));
      coll.set(mergedData);
    }

    if (Array.isArray(data)) {
      coll.set(data);
    }

    return coll;
  }, [data]);

  const columns = useMemo(() => {
    if (collection.size === 0) {
      return [];
    }

    const firstModel = collection.models[0];
    const modelData = firstModel.toJS();

    return _keys(modelData)
      .filter((key) => !_isObject(modelData[key]))
      .slice(0, 8)
      .map((key) => ({
        label: _startCase(key),
        name: key,
        renderer: ({ value }) => {
          if (key.toLowerCase().includes('id')) {
            return <CopyText text={value} />;
          }

          return <Text>{String(value || '')}</Text>;
        }
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection.id]);

  const formatQuery = (q) => {
    if (!q || typeof q !== 'string') {
      return q;
    }
    // Collapse multiple spaces around pipes first for consistency
    const normalized = q.replace(/\s*\|\s*/g, ' | ').trim();
    // Split on pipe keeping segments, then reassemble with newline + indentation before each pipe (except first segment)
    return normalized
      .split('|')
      .map((seg, idx) => {
        const trimmed = seg.trim();
        return idx === 0 ? trimmed : `  | ${trimmed}`; // two-space indent before each pipe segment
      })
      .join('\n');
  };

  return (
    <ToolResultCard
      tool={tool}
      onCloseToolResult={onCloseToolResult}
      isInlineMode={isInlineMode}
      className="tool-result-azure-inventory"
      title={`Resource Graph query executed for subscriptions ${subscription_ids.join(', ')}`}
    >
      <Flex flexDirection="column" px={1} pb={1} flex={1} gap={2}>
        <Flex flexDirection="column" px={1} pb={1} flex={1}>
          <Flex alignItems="center" justifyContent="space-between" p={2} pb={0}>
            <Heading level={4} alignSelf="flex-start">
              Results:
            </Heading>
            <Button
              text="Show Query"
              small
              icon={FaEllipsisH}
              onClick={() => setShowQuery((prev) => !prev)}
              color="muted"
              minimal
            />
          </Flex>
          <AdminTable collection={collection} columns={columns} maxHeight="400px" />
        </Flex>
        {showQuery && (
          <Flex flexDirection="column" px={1} pb={1} flex={1}>
            <Flex alignItems="center" justifyContent="space-between" p={2} pb={0}>
              <Heading level={4} alignSelf="flex-start">
                Resource Graph Query:
              </Heading>
              <Button text="Close" small icon="cross" onClick={() => setShowQuery(false)} color="muted" minimal />
            </Flex>
            <ConfigSnippet
              source={formatQuery(query)}
              themeName="steel"
              p={2}
              flex
              overflow="hidden"
              maxHeight="300px"
            />
          </Flex>
        )}
      </Flex>
    </ToolResultCard>
  );
};

export default AzureResourceGraphToolResult;
